<template>
  <v-container>
    <v-row class="justify-center">
      <v-col cols="12" sm="11" lg="11" :class="['px-0']">
        <!-- Sticky Header with Filters -->
        <div class="table-header-filters">
          <v-row dense class="align-center">
            <v-col cols="12" md="6">
              <h2 class="gradient-text">Izlazni e-računi</h2>
            </v-col>
            <v-col cols="12" md="6" class="text-right">
              <div class="d-flex align-center justify-end">
                <!-- Skriven gumb "Pošaljite dokument" -->
                <v-btn v-if="false" color="success" @click="showSendDocumentDialog" class="mr-2 send-doc-btn" small depressed>
                  <v-icon small left>mdi-send</v-icon>
                  Pošaljite dokument
                </v-btn>
                <DocumentFilters
                  :filters="filters"
                  @filters-changed="onFiltersChanged"
                />
                <v-btn color="primary" @click="refreshData" :loading="loading" class="ml-2 refresh-btn" small depressed>
                  <v-icon small left>mdi-refresh</v-icon>
                  Osvježi
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- Data Table -->
        <v-card>
          <v-data-table
            :headers="headers"
            :items="documents"
            :loading="loading"
            :footer-props="{
              'items-per-page-options': [10, 25, 50, 100],
            }"
            class="elevation-1"
          >
            <!-- Document Type column -->
            <template v-slot:[`item.documentType`]="{ item }">
              {{ formatDocumentType(item.documentType) }}
            </template>

            <!-- Status column -->
            <template v-slot:[`item.status`]="{ item }">
              <div :class="getStatusClass(item.status)">
                {{ getStatusText(item.status) }}
              </div>
            </template>

            <!-- Date columns -->
            <template v-slot:[`item.issuedOn`]="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    {{ formatDateOnly(item.issuedOn) }}
                  </span>
                </template>
                <span>{{ formatDateTime(item.issuedOn) }}</span>
              </v-tooltip>
            </template>

            <template v-slot:[`item.insertedOn`]="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    {{ formatDateOnly(item.insertedOn) }}
                  </span>
                </template>
                <span>{{ formatDateTime(item.insertedOn) }}</span>
              </v-tooltip>
            </template>

            <!-- Amount column -->
            <template v-slot:[`item.amount`]="{ item }">
              <span v-if="item.amount !== null && item.amount !== undefined">
                {{ formatCurrency(item.amount, item.currency) }}
              </span>
              <span v-else class="text--secondary">N/A</span>
            </template>

            <!-- Actions column -->
            <template v-slot:[`item.actions`]="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    @click="showDocumentDetails(item)"
                    class="action-icon-btn"
                  >
                    <v-icon small>mdi-eye</v-icon>
                  </v-btn>
                </template>
                <span>Prikaži detalje</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    @click="canChangeStatus(item) ? showStatusUpdateDialog(item) : null"
                    :disabled="!canChangeStatus(item)"
                    class="action-icon-btn"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>{{ getStatusTooltip(item) }}</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    @click="viewPdf(item)"
                    class="action-icon-btn"
                  >
                    <v-icon small>mdi-file-pdf-box</v-icon>
                  </v-btn>
                </template>
                <span>Prikaži PDF</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    @click="viewXml(item)"
                    class="action-icon-btn"
                  >
                    <v-icon small>mdi-code-tags</v-icon>
                  </v-btn>
                </template>
                <span>Prikaži XML</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    @click="showEReporting(item)"
                    class="action-icon-btn"
                  >
                    <v-icon small>mdi-file-chart</v-icon>
                  </v-btn>
                </template>
                <span>eIzvještavanje</span>
              </v-tooltip>

              <!-- Skriven gumb "Pošaljite ponovo" -->
              <v-tooltip bottom v-if="false && canResendDocument(item)">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    @click="resendDocument(item)"
                    :loading="resending[item.id]"
                    class="action-icon-btn action-icon-btn--success"
                  >
                    <v-icon small>mdi-send</v-icon>
                  </v-btn>
                </template>
                <span>Pošaljite ponovo</span>
              </v-tooltip>
            </template>

            <!-- No data slot -->
            <template v-slot:no-data>
              <div class="no-data-container">
                <div class="no-data-icon">
                  <v-icon size="64" color="rgba(57, 137, 201, 0.3)">mdi-file-document-outline</v-icon>
                </div>
                <div class="no-data-title">Nema dostupnih dokumenata</div>
                <div class="no-data-subtitle">Pokušajte promijeniti filtere ili provjerite kasnije</div>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Document Detail Dialog -->
    <DocumentDetailDialog v-model="detailDialog" :document="selectedDocument" />

    <!-- Status Update Dialog -->
    <StatusUpdateDialog
      v-model="statusDialog"
      :document-id="selectedDocument ? selectedDocument.id : null"
      :current-status="selectedDocument ? selectedDocument.status : null"
      :document-amount="selectedDocument ? selectedDocument.amount : 0"
      document-type="outgoing"
      @update-status="updateDocumentStatus"
      @error="showError"
    />

    <!-- Send Document Dialog -->
    <SendDocumentDialog
      v-model="sendDialog"
      @send-document="sendDocument"
      @error="showError"
    />

    <!-- PDF Viewer Dialog -->
    <PdfViewerDialog
      v-model="pdfDialog"
      :document-id="selectedDocument ? selectedDocument.id : null"
      :document-name="
        selectedDocument ? `dokument_${selectedDocument.id}` : 'dokument'
      "
      @error="showError"
    />

    <!-- XML Viewer Dialog -->
    <XmlViewerDialog
      v-model="xmlDialog"
      :document-id="selectedDocument ? selectedDocument.id : null"
      :document-name="
        selectedDocument ? `dokument_${selectedDocument.id}` : 'dokument'
      "
      @error="showError"
    />

    <!-- eReporting Dialog -->
    <EReportingDialog ref="eReportingDialog" />

    <!-- Error Snackbar -->
    <v-snackbar v-model="errorSnackbar" color="error" :timeout="-1" top>
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="errorSnackbar = false">
          Zatvori
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Success Snackbar -->
    <v-snackbar v-model="successSnackbar" color="success" :timeout="4000" top>
      {{ successMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { eracunApi } from '@/api/eracun'
import { ereportingApi } from '@/api/ereporting'
import DocumentDetailDialog from './shared/DocumentDetailDialog.vue'
import StatusUpdateDialog from './shared/StatusUpdateDialog.vue'
import DocumentFilters from './shared/DocumentFilters.vue'
import SendDocumentDialog from './shared/SendDocumentDialog.vue'
import PdfViewerDialog from './shared/PdfViewerDialog.vue'
import XmlViewerDialog from './shared/XmlViewerDialog.vue'
import EReportingDialog from './shared/EReportingDialog.vue'
import { DateTime } from 'luxon'
import state from '@/state'

export default {
  name: 'OutgoingEInvoices',
  components: {
    DocumentDetailDialog,
    StatusUpdateDialog,
    DocumentFilters,
    SendDocumentDialog,
    PdfViewerDialog,
    XmlViewerDialog,
    EReportingDialog
  },
  data () {
    const today = new Date().toISOString().split('T')[0]
    return {
      loading: false,
      documents: [],
      totalItems: 0,
      filters: {
        insertedFrom: today
      },
      headers: [
        { text: 'ID', value: 'id', width: 120 },
        { text: 'Tip dokumenta', value: 'documentType', sortable: false },
        { text: 'Kupac', value: 'customerPartyName', sortable: false },
        { text: 'Datum izdavanja', value: 'issuedOn', width: 110 },
        { text: 'Ukupan iznos', value: 'amount', align: 'end' },
        { text: 'Status', value: 'status', width: 180 },
        { text: 'Datum unosa', value: 'insertedOn', width: 110 },
        { text: 'Akcije', value: 'actions', sortable: false, width: 260 }
      ],
      detailDialog: false,
      statusDialog: false,
      sendDialog: false,
      pdfDialog: false,
      xmlDialog: false,
      selectedDocument: null,
      downloadingXml: {},
      resending: {},
      errorSnackbar: false,
      errorMessage: '',
      successSnackbar: false,
      successMessage: ''
    }
  },
  mounted () {
    this.fetchDocuments()
  },
  methods: {
    async fetchDocuments () {
      this.loading = true
      try {
        const pagination = {
          page: 1,
          limit: 1000
        }

        const allFilters = {
          ...this.filters,
          companyId: state.getCurrentCompany().id
        }

        const response = await eracunApi.getDocuments('outgoing', allFilters, pagination)

        this.documents = response.data || []
      } catch (error) {
        console.error('Error fetching documents:', error)
        this.showError('Greška pri dohvaćanju dokumenata')
      } finally {
        this.loading = false
      }
    },

    onFiltersChanged (newFilters) {
      this.filters = newFilters
      this.fetchDocuments()
    },

    refreshData () {
      this.fetchDocuments()
    },

    showDocumentDetails (document) {
      this.selectedDocument = document
      this.detailDialog = true
    },

    showStatusUpdateDialog (document) {
      this.selectedDocument = document
      this.statusDialog = true
    },

    showSendDocumentDialog () {
      this.sendDialog = true
    },

    async updateDocumentStatus ({ documentId, payload, updateMask }) {
      try {
        const companyId = state.getCurrentCompany().id
        const document = this.documents.find(doc => doc.id === documentId)

        if (!document) {
          this.showError('Dokument nije pronađen')
          return
        }

        // Koristimo eReporting API za označavanje plaćanja izlaznih računa
        if (payload.status === 5) {
          // Status 5 = Plaćen (označava kao plaćen)
          const reportingPayload = {
            id: document.id.toString(),
            documentId: document.documentNumber || document.id.toString(),
            issueDate: document.issuedOn ? document.issuedOn.split('T')[0] : new Date().toISOString().split('T')[0],
            paidAmount: payload.paidAmount || document.amount || 0,
            paymentDate: payload.paymentDate || new Date().toISOString().split('T')[0],
            paymentType: payload.paymentType || 1,
            customerPartyId: document.customerPartyId || '',
            supplierPartyId: document.supplierPartyId || companyId
          }
          await ereportingApi.markAsPaidById(document.id, reportingPayload)
          this.showSuccess('Dokument je uspješno označen kao plaćen')
        } else {
          // Za ostale statuse koristimo standardni eracun API
          await eracunApi.updateDocumentStatus(documentId, payload, companyId, updateMask)
          this.showSuccess('Status dokumenta je uspješno ažuriran')
        }

        // Osvježavamo podatke nakon API poziva
        this.fetchDocuments()
      } catch (error) {
        console.error('Error updating document status:', error)
        const errorMessage = error.parsedMessage || error.response?.data?.message || 'Greška pri ažuriranju statusa dokumenta'
        this.showError(errorMessage)
      }
    },

    async sendDocument ({ payload, validate }) {
      try {
        const companyId = state.getCurrentCompany().id
        const payloadWithCompany = { ...payload, companyId }

        if (validate) {
          await eracunApi.validateDocument(payloadWithCompany)
          this.showSuccess('Dokument je uspješno validiran')
        } else {
          await eracunApi.sendDocument(payloadWithCompany)
          this.showSuccess('Dokument je uspješno poslan')
          this.fetchDocuments()
        }
      } catch (error) {
        console.error('Error sending/validating document:', error)
        const action = validate ? 'validaciji' : 'slanju'
        this.showError(`Greška pri ${action} dokumenta`)
      }
    },

    viewPdf (document) {
      this.selectedDocument = document
      this.pdfDialog = true
    },

    viewXml (document) {
      this.selectedDocument = document
      this.xmlDialog = true
    },

    showEReporting (document) {
      this.$refs.eReportingDialog.show(document)
    },

    async downloadXml (document) {
      this.$set(this.downloadingXml, document.id, true)
      try {
        const companyId = state.getCurrentCompany().id
        const xmlData = await eracunApi.getDocumentXml(document.id, companyId)
        const blob = new Blob([xmlData], { type: 'application/xml' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${document.id}.xml`
        link.click()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Error downloading XML:', error)
        this.showError('Greška pri preuzimanju XML-a')
      } finally {
        this.$set(this.downloadingXml, document.id, false)
      }
    },

    async resendDocument (document) {
      this.$set(this.resending, document.id, true)
      try {
        const companyId = state.getCurrentCompany().id
        await eracunApi.sendDocument({ documentId: document.id, companyId })
        this.showSuccess('Dokument je uspješno poslan ponovo')
        this.fetchDocuments()
      } catch (error) {
        console.error('Error resending document:', error)
        this.showError('Greška pri ponovnom slanju dokumenta')
      } finally {
        this.$set(this.resending, document.id, false)
      }
    },

    canResendDocument (document) {
      return [4, 7].includes(document.status)
    },

    formatDate (dateString) {
      if (!dateString) return 'N/A'
      try {
        return DateTime.fromISO(dateString).setLocale('hr').toFormat('dd.MM.yyyy HH:mm')
      } catch (error) {
        return dateString
      }
    },

    formatDateOnly (dateString) {
      if (!dateString) return 'N/A'
      try {
        return DateTime.fromISO(dateString).setLocale('hr').toFormat('dd.MM.yyyy')
      } catch (error) {
        return dateString
      }
    },

    formatDateTime (dateString) {
      if (!dateString) return 'N/A'
      try {
        return DateTime.fromISO(dateString).setLocale('hr').toFormat('dd.MM.yyyy HH:mm:ss')
      } catch (error) {
        return dateString
      }
    },

    formatCurrency (amount, currency = 'EUR') {
      if (amount === null || amount === undefined) return 'N/A'
      return new Intl.NumberFormat('hr-HR', {
        style: 'currency',
        currency: currency
      }).format(amount)
    },

    formatDocumentType (type) {
      const typeMapping = {
        Invoice: 'Račun',
        CreditNote: 'Knjižno odobrenje',
        DebitNote: 'Terećenje'
      }
      return typeMapping[type] || type
    },

    getStatusClass (status) {
      const statusClasses = {
        1: 'status-pending-exp', // Na čekanju
        2: 'status-processing-exp', // Na slanju
        3: 'status-ok-exp', // Poslan
        4: 'status-delivered-exp', // Isporučen
        5: 'status-paid-exp', // Plaćen
        6: 'status-rejected-exp', // Odbijen
        7: 'status-paid-exp', // Plaćen u potpunosti
        8: 'status-partial-exp', // Parcijalno plaćen
        9: 'status-failed-exp' // Neuspješna isporuka
      }
      return statusClasses[status] || 'status-pending-exp'
    },

    getStatusColor (status) {
      const statusColors = {
        1: 'blue',
        2: 'orange',
        3: 'green',
        4: 'red',
        5: 'teal',
        6: 'indigo',
        7: 'teal',
        8: 'brown',
        9: 'grey'
      }
      return statusColors[status] || 'grey'
    },

    getStatusText (status) {
      const statusTexts = {
        1: 'Na čekanju',
        2: 'Na slanju',
        3: 'Poslan',
        4: 'Isporučen',
        5: 'Plaćen',
        6: 'Odbijen',
        7: 'Plaćen u potpunosti',
        8: 'Parcijalno plaćen',
        9: 'Neuspješna isporuka'
      }
      return statusTexts[status] || `Status ${status}`
    },

    canChangeStatus (item) {
      // Dozvoliti promjenu statusa za sve statuse u izlaznim računima
      return true
    },

    getStatusTooltip (item) {
      return 'eIzvještavanje (Plaćen/Odbijen)'
    },

    showError (message) {
      this.errorMessage = message
      this.errorSnackbar = true
    },

    showSuccess (message) {
      this.successMessage = message
      this.successSnackbar = true
    }
  }
}
</script>

<style scoped>
/* Gradient text for title */
.gradient-text {
  background: linear-gradient(135deg, rgba(0, 179, 190, 1) 0%, rgba(57, 137, 201, 1) 50%, rgba(97, 84, 164, 1) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0;
  padding: 0;
}

/* Sticky header with filters */
.table-header-filters {
  position: sticky;
  top: 64px;
  z-index: 2;
  background: linear-gradient(
    135deg,
    rgba(0, 179, 190, 0.08) 0%,
    rgba(57, 137, 201, 0.06) 50%,
    rgba(97, 84, 164, 0.08) 100%
  );
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  padding: 16px 20px;
  margin-bottom: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* Header button styling */
.page-nav-btn-header {
  height: 32px !important;
  font-size: 0.875rem !important;
  text-transform: none !important;
  letter-spacing: 0.02em !important;
  transition: all 0.12s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

/* Send document button styling */
.send-doc-btn {
  height: 36px !important;
  font-size: 0.875rem !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: 0.02em !important;
  border-radius: 8px !important;
  padding: 0 16px !important;
  background: linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%) !important;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.25) !important;
  transition: all 0.12s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.send-doc-btn:hover {
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.35) !important;
  transform: translateY(-1px);
}

.send-doc-btn:active {
  transform: translateY(0px) scale(0.98);
}

.send-doc-btn .v-icon {
  transition: transform 0.12s ease !important;
}

.send-doc-btn:hover .v-icon {
  transform: translateX(2px);
}

/* Refresh button styling */
.refresh-btn {
  height: 36px !important;
  font-size: 0.875rem !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: 0.02em !important;
  border-radius: 8px !important;
  padding: 0 16px !important;
  background: linear-gradient(135deg, #3989C9 0%, #00B3BE 100%) !important;
  box-shadow: 0 2px 8px rgba(57, 137, 201, 0.25) !important;
  transition: all 0.12s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.refresh-btn:hover {
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.35) !important;
  transform: translateY(-1px);
}

.refresh-btn:active {
  transform: translateY(0px) scale(0.98);
}

.refresh-btn .v-icon {
  transition: transform 0.3s ease !important;
}

.refresh-btn:hover .v-icon {
  transform: rotate(180deg);
}

/* Status indicators with glass morphism */
.status-pending-exp,
.status-processing-exp,
.status-ok-exp,
.status-delivered-exp,
.status-accepted-exp,
.status-rejected-exp,
.status-paid-exp,
.status-partial-exp,
.status-failed-exp {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-left: 3px solid;
  transition: all 0.15s ease;
}

/* Status: Na čekanju (Pending) */
.status-pending-exp {
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.15), rgba(33, 150, 243, 0.08));
  border-left-color: #2196F3;
  color: #1976D2;
}

/* Status: Na slanju (Processing) */
.status-processing-exp {
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.15), rgba(255, 152, 0, 0.08));
  border-left-color: #FF9800;
  color: #F57C00;
}

/* Status: Poslan (Sent/OK) */
.status-ok-exp {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.15), rgba(76, 175, 80, 0.08));
  border-left-color: #4CAF50;
  color: #388E3C;
}

/* Status: Isporučen (Delivered) */
.status-delivered-exp {
  background: linear-gradient(135deg, rgba(0, 179, 190, 0.15), rgba(0, 179, 190, 0.08));
  border-left-color: #00B3BE;
  color: #00838F;
}

/* Status: Prihvaćen (Accepted) */
.status-accepted-exp {
  background: linear-gradient(135deg, rgba(0, 150, 136, 0.15), rgba(0, 150, 136, 0.08));
  border-left-color: #009688;
  color: #00695C;
}

/* Status: Odbijen (Rejected) */
.status-rejected-exp {
  background: linear-gradient(135deg, rgba(103, 58, 183, 0.15), rgba(103, 58, 183, 0.08));
  border-left-color: #673AB7;
  color: #512DA8;
}

/* Status: Plaćen u potpunosti (Fully Paid) */
.status-paid-exp {
  background: linear-gradient(135deg, rgba(0, 179, 190, 0.15), rgba(57, 137, 201, 0.08));
  border-left-color: #3989C9;
  color: #2C6BA0;
}

/* Status: Parcijalno plaćen (Partially Paid) */
.status-partial-exp {
  background: linear-gradient(135deg, rgba(121, 85, 72, 0.15), rgba(121, 85, 72, 0.08));
  border-left-color: #795548;
  color: #5D4037;
}

/* Status: Neuspješna isporuka (Failed Delivery) */
.status-failed-exp {
  background: linear-gradient(135deg, rgba(244, 67, 54, 0.15), rgba(244, 67, 54, 0.08));
  border-left-color: #F44336;
  color: #D32F2F;
}

/* No data message */
.no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  text-align: center;
}

.no-data-icon {
  margin-bottom: 24px;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

.no-data-title {
  font-size: 1.5rem;
  font-weight: 600;
  background: linear-gradient(135deg, rgba(0, 179, 190, 1) 0%, rgba(57, 137, 201, 1) 50%, rgba(97, 84, 164, 1) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 8px;
}

.no-data-subtitle {
  font-size: 0.95rem;
  color: rgba(0, 0, 0, 0.5);
  font-weight: 400;
}

/* Action icon buttons in table */
.action-icon-btn {
  width: 36px !important;
  height: 36px !important;
  margin: 0 3px !important;
  border-radius: 8px !important;
  transition: all 0.12s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.action-icon-btn:hover {
  background: rgba(57, 137, 201, 0.1) !important;
  transform: scale(1.08);
}

.action-icon-btn:active {
  transform: scale(0.95);
}

.action-icon-btn.v-btn--disabled {
  opacity: 0.4 !important;
}

.action-icon-btn .v-icon {
  color: rgba(57, 137, 201, 0.85) !important;
  font-size: 20px !important;
  transition: color 0.12s ease !important;
}

.action-icon-btn:hover .v-icon {
  color: #3989C9 !important;
}

.action-icon-btn.v-btn--disabled .v-icon {
  color: rgba(0, 0, 0, 0.26) !important;
}

/* Success variant for resend button */
.action-icon-btn--success:hover {
  background: rgba(76, 175, 80, 0.1) !important;
}

.action-icon-btn--success .v-icon {
  color: rgba(76, 175, 80, 0.85) !important;
}

.action-icon-btn--success:hover .v-icon {
  color: #4CAF50 !important;
}
</style>
